import React from 'react';
import { Mail, KeyRound, Loader2, CheckCircle2, Pencil, ArrowRight, BellRing, LineChart } from 'lucide-react';
import { useAuth } from '../auth/useAuth';
import { FieldCard, FloatingField, StepHeader, accentVars, tintFromAccent } from './_shared';
import Banner from '../shared/Banner';

interface VerifyEmailStepProps {
  email: string;
  onEmailChange: (email: string) => void;
  isVerified: boolean;
  onVerified: () => void | Promise<void>;
  accentColor?: string;
  primaryColor?: string;
  /** Station slug; forwarded to requestOtp so the OTP email picks up
   *  per-station branding (publisher name, logo, accent) instead of the
   *  generic CTV Buyer fallback. */
  stationSlug?: string;
}

const VerifyEmailStep: React.FC<VerifyEmailStepProps> = ({
  email,
  onEmailChange,
  isVerified,
  onVerified,
  accentColor = '#2563eb',
  primaryColor,
  stationSlug,
}) => {
  const { requestOtp, verifyOtp } = useAuth();
  const [stage, setStage] = React.useState<'confirm' | 'edit' | 'code'>('confirm');
  const [draftEmail, setDraftEmail] = React.useState(email);
  const [code, setCode] = React.useState('');
  const [devCode, setDevCode] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    setDraftEmail(email);
  }, [email]);

  const handleEdit = () => {
    setDraftEmail(email);
    setError(null);
    setStage('edit');
  };

  const handleSaveEmail = () => {
    const trimmed = draftEmail.trim();
    if (!trimmed.includes('@') || trimmed.length < 5) {
      setError('Enter a valid email');
      return;
    }
    onEmailChange(trimmed);
    setError(null);
    setStage('confirm');
  };

  const handleSendCode = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await requestOtp(email, stationSlug);
      setDevCode(res.devCode);
      setCode('');
      setStage('code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send code');
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError('Enter the 6-digit code');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await verifyOtp(email, code);
      await onVerified();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  const deep = primaryColor ?? accentColor;
  const primaryButtonStyle: React.CSSProperties = {
    background: `linear-gradient(180deg, ${accentColor}, ${deep})`,
    boxShadow: `0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 16px -8px ${deep}`,
  };

  const PrimaryBtn: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { children: React.ReactNode }> = ({
    children, className = '', ...rest
  }) => (
    <button
      {...rest}
      className={`group inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full text-[13px] font-semibold tracking-tight text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:brightness-100 ${className}`}
      style={primaryButtonStyle}
    >
      {children}
    </button>
  );

  return (
    <div className="-m-4 sm:-m-6 md:-m-8" style={accentVars(accentColor, primaryColor)}>
      <div className="px-6 sm:px-10 md:px-14 pt-6 pb-6">
        <StepHeader step={4} label="Verify" accentColor={accentColor} />
      </div>

      <div className="px-6 sm:px-10 md:px-14 pb-10 space-y-5">
        {!isVerified && (
          <div className="px-1 -mt-2">
            <p className="text-[13px] text-slate-600 leading-relaxed mb-3">
              We use your email for two things — please confirm it's correct.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <ReasonRow
                icon={BellRing}
                label="Stay in touch"
                description="Approval, launch, and support updates."
                accentColor={accentColor}
              />
              <ReasonRow
                icon={LineChart}
                label="Track your campaign"
                description="Sign in to see live performance reports."
                accentColor={accentColor}
              />
            </div>
          </div>
        )}

        {isVerified ? (
          <Banner tone="success" label={email} icon={<CheckCircle2 className="w-3 h-3" />}>
            Verified — you're all set.
          </Banner>
        ) : (
          <>
            {stage === 'confirm' && (
              <>
                <FieldCard accentColor={accentColor}>
                  <div className="px-5 py-4 flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                      style={{ backgroundColor: tintFromAccent(accentColor, 0.1), color: accentColor }}
                    >
                      <Mail className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">Your email</div>
                      <div className="text-[14px] font-semibold tracking-tight text-slate-900 truncate">
                        {email || '(none)'}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleEdit}
                      className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-[0.08em] text-slate-500 hover:text-slate-900 px-2.5 py-1 rounded-full hover:bg-slate-100 transition-colors"
                    >
                      <Pencil className="w-3 h-3" />
                      Edit
                    </button>
                  </div>
                </FieldCard>
                {error && <p className="text-xs text-red-500 px-1">{error}</p>}
                <PrimaryBtn type="button" onClick={handleSendCode} disabled={busy || !email.includes('@')}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Send verification code
                  {!busy && <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />}
                </PrimaryBtn>
              </>
            )}

            {stage === 'edit' && (
              <>
                <FieldCard accentColor={accentColor}>
                  <FloatingField id="verifyEmailInput" label="Email" required>
                    {(p) => (
                      <input
                        {...p}
                        type="email"
                        autoFocus
                        value={draftEmail}
                        onChange={(e) => setDraftEmail(e.target.value)}
                        placeholder="you@business.com"
                      />
                    )}
                  </FloatingField>
                </FieldCard>
                {error && <p className="text-xs text-red-500 px-1">{error}</p>}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => { setStage('confirm'); setError(null); }}
                    className="px-3 py-2 rounded-full text-[13px] font-medium tracking-tight text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <PrimaryBtn type="button" onClick={handleSaveEmail}>
                    Save
                  </PrimaryBtn>
                </div>
              </>
            )}

            {stage === 'code' && (
              <form onSubmit={handleVerify} className="space-y-5">
                <p className="text-[13px] text-slate-600 px-1">
                  We sent a 6-digit code to{' '}
                  <span className="font-semibold text-slate-900">{email}</span>.
                </p>

                {devCode && (
                  <Banner tone="caution" label="Demo mode">
                    Your code is{' '}
                    <span className="font-mono font-bold text-[13px] text-amber-950">{devCode}</span>
                    {' '}— paste it below.
                  </Banner>
                )}

                <FieldCard accentColor={accentColor}>
                  <div className="relative px-5 py-4 transition-colors focus-within:bg-[color:var(--accent-tint)] group">
                    <span
                      aria-hidden
                      className="absolute left-0 top-3 bottom-3 w-[2px] rounded-r bg-[color:var(--accent)] opacity-0 group-focus-within:opacity-100 transition-opacity"
                    />
                    <label
                      htmlFor="verify-code"
                      className="block text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400 mb-2 group-focus-within:text-[color:var(--accent)]"
                    >
                      6-digit code
                    </label>
                    <div className="flex items-center gap-2">
                      <KeyRound className="w-4 h-4 text-slate-400" />
                      <input
                        id="verify-code"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]{6}"
                        maxLength={6}
                        autoFocus
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        className="w-full bg-transparent border-0 px-0 py-1 text-[20px] font-mono tracking-[0.4em] text-slate-900 placeholder-slate-300 caret-[color:var(--accent)] focus:outline-none focus:ring-0"
                      />
                    </div>
                  </div>
                </FieldCard>

                {error && <p className="text-xs text-red-500 px-1">{error}</p>}

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => { setStage('confirm'); setCode(''); setError(null); }}
                    className="px-3 py-2 rounded-full text-[13px] font-medium tracking-tight text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={busy}
                    className="px-3 py-2 rounded-full text-[13px] font-medium tracking-tight text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors disabled:opacity-50"
                  >
                    Resend code
                  </button>
                  <PrimaryBtn type="submit" disabled={busy} className="ml-auto">
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                    Verify
                  </PrimaryBtn>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

interface ReasonRowProps {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  label: string;
  description: string;
  accentColor: string;
}

const ReasonRow: React.FC<ReasonRowProps> = ({ icon: Icon, label, description, accentColor }) => (
  <div className="flex items-start gap-2.5 py-1.5">
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
      style={{ backgroundColor: tintFromAccent(accentColor, 0.08), color: accentColor }}
    >
      <Icon className="w-3.5 h-3.5" />
    </div>
    <div className="min-w-0">
      <div className="text-[12px] font-semibold tracking-tight text-slate-900">{label}</div>
      <div className="text-[12px] text-slate-500 leading-snug">{description}</div>
    </div>
  </div>
);

export default VerifyEmailStep;
